import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { env, firebaseConfigured } from '../config/env';

/**
 * Firebase, loaded on demand.
 *
 * The SDK is around 155 kB gzipped, and the one route that matters most for
 * reach — a shared map opened by someone who will never sign in — needs none
 * of it. Every entry point here is a dynamic import, so Firebase lands in an
 * async chunk that the public view never requests. It is fetched when the
 * header mounts a sign-in state or the history page opens.
 *
 * The type imports above are erased at build time and pull in nothing.
 */

let appPromise: Promise<FirebaseApp> | null = null;
let authPromise: Promise<Auth> | null = null;
let dbPromise: Promise<Firestore> | null = null;
let emulatorsConnected = false;

function notConfigured(): Error {
  return new Error('Firebase is not configured; see VITE_FIREBASE_* in .env.example');
}

async function loadApp(): Promise<FirebaseApp> {
  if (!firebaseConfigured) throw notConfigured();
  if (!appPromise) {
    appPromise = (async () => {
      const { initializeApp } = await import('firebase/app');
      return initializeApp({
        apiKey: env.firebase.apiKey,
        authDomain: env.firebase.authDomain,
        projectId: env.firebase.projectId,
        storageBucket: env.firebase.storageBucket,
        messagingSenderId: env.firebase.messagingSenderId,
        appId: env.firebase.appId,
        ...(env.firebase.measurementId ? { measurementId: env.firebase.measurementId } : {}),
      });
    })();
  }
  return appPromise;
}

export function loadAuth(): Promise<Auth> {
  if (!authPromise) {
    authPromise = (async () => {
      const [{ getAuth, connectAuthEmulator }, app] = await Promise.all([
        import('firebase/auth'),
        loadApp(),
      ]);
      const auth = getAuth(app);
      if (env.useFirebaseEmulators) {
        connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      }
      return auth;
    })();
  }
  return authPromise;
}

export function loadDb(): Promise<Firestore> {
  if (!dbPromise) {
    dbPromise = (async () => {
      const [{ getFirestore, connectFirestoreEmulator }, app] = await Promise.all([
        import('firebase/firestore'),
        loadApp(),
      ]);
      const db = getFirestore(app);
      if (env.useFirebaseEmulators && !emulatorsConnected) {
        connectFirestoreEmulator(db, '127.0.0.1', 8080);
        emulatorsConnected = true;
      }
      return db;
    })();
  }
  return dbPromise;
}

export { firebaseConfigured };
